import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { QueryFetchFunction, QueryTransformer } from 'common/helpers/common';
import {
  ApiError,
  ApiResult,
  ExtendedApiResult,
  ImageClass,
} from 'common/repositories/common.model';

import {
  BookingCountModel,
  BookingLiteModel,
  BookingModel,
  BookingPaymentModel,
  BookingShowFormModel,
  BookingTicketModel,
  getBookingInput,
  getBookingsInput,
  PaymentFeeModel,
  RefundModel,
} from './model';

export function useGetBookings(
  input?: getBookingsInput,
  options?: UseInfiniteQueryOptions<
    ExtendedApiResult<BookingLiteModel[]>,
    ApiError,
    ExtendedApiResult<BookingLiteModel[]>,
    any
  >,
): UseInfiniteQueryResult<ExtendedApiResult<BookingLiteModel[]>, ApiError> {
  return QueryTransformer(
    useInfiniteQuery(
      ['getBookings', input?.params],
      async ({ pageParam = 1 }) =>
        QueryFetchFunction({
          url: 'bookings',
          params: { page: pageParam, ...input?.params },
        }),
      options,
    ),
    BookingLiteModel,
  );
}

export function useGetRefund(
  input?: getBookingInput,
  options?: UseQueryOptions<ApiResult<RefundModel>, ApiError>,
): UseQueryResult<ApiResult<RefundModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getRefund', input?.bookingId],
      () => QueryFetchFunction({ url: `bookings/${input?.bookingId}/refund` }),
      options,
    ),
    RefundModel,
  );
}
export function getBookingsKey(input?: getBookingsInput) {
  const keys: any[] = ['getBookings'];
  if (input) {
    keys.push(input?.params);
  }
  return keys;
}

export function useGetBooking(
  input?: getBookingInput,
  options?: UseQueryOptions<ExtendedApiResult<BookingModel>, ApiError>,
): UseQueryResult<ExtendedApiResult<BookingModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBooking', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}`,
        }),
      options,
    ),
    BookingModel,
  );
}

export function useGetBookingForm(
  input?: getBookingInput,
  options?: UseQueryOptions<ExtendedApiResult<BookingShowFormModel>, ApiError>,
): UseQueryResult<ExtendedApiResult<BookingShowFormModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBookingForm', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}/form`,
        }),
      options,
    ),
    BookingShowFormModel,
  );
}

export function useGetBookingPayment(
  input?: getBookingInput,
  options?: UseQueryOptions<ApiResult<BookingPaymentModel>, ApiError>,
): UseQueryResult<ApiResult<BookingPaymentModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBookingPayment', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}/bill`,
        }),
      options,
    ),
    BookingPaymentModel,
  );
}

export function useGetBookingTicket(
  input?: getBookingInput,
  options?: UseQueryOptions<ApiResult<BookingTicketModel[]>, ApiError>,
): UseQueryResult<ApiResult<BookingTicketModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBookingTicket', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}/tickets`,
        }),
      { staleTime: Infinity, cacheTime: Infinity, ...options },
    ),
    BookingTicketModel,
  );
}

export function useGetBookingCount(
  options?: UseQueryOptions<ApiResult<BookingCountModel>, ApiError>,
): UseQueryResult<ApiResult<BookingCountModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getBookingCount'],
      () =>
        QueryFetchFunction({
          url: `bookings/count`,
        }),
      options,
    ),
    BookingCountModel,
  );
}

export function useGetPaymentFee(
  input?: getBookingInput,
  options?: UseQueryOptions<ApiResult<PaymentFeeModel>, ApiError>,
): UseQueryResult<ApiResult<PaymentFeeModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getPaymentFee', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}/payment-fee`,
        }),
      options,
    ),
    PaymentFeeModel,
  );
}

export function useGetRefundProof(
  input?: getBookingInput,
  options?: UseQueryOptions<ApiResult<ImageClass[]>, ApiError>,
): UseQueryResult<ApiResult<ImageClass[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      ['getRefundProof', input?.bookingId],
      () =>
        QueryFetchFunction({
          url: `bookings/${input?.bookingId}/refund-proof`,
        }),
      { staleTime: Infinity, cacheTime: Infinity, ...options },
    ),
    ImageClass,
  );
}

export function getBookingKey(input?: getBookingInput) {
  const keys: any[] = ['getBooking', input?.bookingId];
  return keys;
}

export function getBookingFormKey(input?: getBookingInput) {
  const keys: any[] = ['getBookingForm', input?.bookingId];
  return keys;
}

export function getBookingPaymentKey(input?: getBookingInput) {
  return ['getBookingPayment', input?.bookingId];
}

export function getBookingTicketKey(input?: getBookingInput) {
  const keys: any[] = ['getBookingTicket', input?.bookingId];
  return keys;
}
export function getRefundBookingKey(input?: getBookingInput) {
  const keys: any[] = ['getRefund', input?.bookingId];
  return keys;
}
export function getPaymentFeeKey(input?: getBookingInput) {
  const keys: any[] = ['getPaymentFee', input?.bookingId];
  return keys;
}
